import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';

import { useL10n, useStore } from '@uplift/hooks';
import { ROUTING_CONFIG } from '@uplift/constants';
import { betterhelpSsoExchange, betterhelpSsoLinkConfirm } from '@uplift/api';
import { Button, Spinner } from '@uplift/ui-v2';

import { environment } from '../../../environments/environment';

const EXCHANGE_TIMEOUT_MS = 10000;

type LandingState =
  | { kind: 'loading' }
  | { kind: 'link_pending'; intent: string; redirectTo: string | null }
  | { kind: 'error' };

// redirect_to is already host-allowlisted by the backend before it reaches the
// landing page; the FE only rejects dangerous schemes (javascript:, data:, ...).
const safeRedirect = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);

    return url.protocol === 'https:' || url.protocol === 'http:' ? raw : null;
  } catch {
    return null;
  }
};

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  panel: {
    maxWidth: '432px',
    width: '100%',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '48px',
    color: theme.palette.grey[700],
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  signingInText: {
    marginTop: theme.spacing(3),
  },
  panelTitle: {
    marginBottom: theme.spacing(2),
  },
}));

export const BetterhelpSsoLandingPage = observer(() => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const betterhelpSsoL10n = l10n.auth.betterhelpSso;
  const { authStore } = useStore();
  const history = useHistory();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [ location.search ]);
  // guards against React StrictMode double-invocation consuming the single-use code
  const startedRef = useRef(false);

  const [ state, setState ] = useState<LandingState>({ kind: 'loading' });

  const routeIntoApp = (redirectTo: string | null) => {
    const safe = safeRedirect(redirectTo);

    if (safe) {
      window.location.assign(safe);
    } else {
      history.replace(ROUTING_CONFIG.main);
    }
  };

  const completeExchange = async (code: string, redirectTo: string | null) => {
    try {
      const { data } = await betterhelpSsoExchange(code);

      authStore.setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      authStore.setTokensInLocalStorage();
      routeIntoApp(redirectTo);
    } catch {
      setState({ kind: 'error' });
    }
  };

  const scrubUrl = () => {
    window.history.replaceState({}, '', location.pathname);
  };

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    if (params.get('error')) {
      setState({ kind: 'error' });
      return;
    }

    const outcome = params.get('outcome');
    const redirectTo = params.get('redirect_to');

    if (outcome === 'signed_in') {
      const code = params.get('code');

      scrubUrl();
      if (!code) {
        setState({ kind: 'error' });
        return;
      }
      completeExchange(code, redirectTo);
      return;
    }

    if (outcome === 'link_pending') {
      const intent = params.get('intent');

      scrubUrl();
      if (!intent) {
        setState({ kind: 'error' });
        return;
      }
      setState({ kind: 'link_pending', intent, redirectTo });
      return;
    }

    setState({ kind: 'error' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.kind !== 'loading') {
      return undefined;
    }
    const timer = setTimeout(() => {
      setState((prev) => (prev.kind === 'loading' ? { kind: 'error' } : prev));
    }, EXCHANGE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [ state.kind ]);

  const handleConfirmLink = async (intent: string, redirectTo: string | null) => {
    setState({ kind: 'loading' });
    try {
      const { data } = await betterhelpSsoLinkConfirm(intent);

      await completeExchange(data.code, redirectTo);
    } catch {
      setState({ kind: 'error' });
    }
  };

  const backToLogin = () => {
    window.location.assign(environment.authUrl);
  };

  if (state.kind === 'loading') {
    return (
      <Box className={classes.root}>
        <Spinner />
        <Typography className={classes.signingInText} variant="body1">{betterhelpSsoL10n.signingIn}</Typography>
      </Box>
    );
  }

  if (state.kind === 'link_pending') {
    const { intent, redirectTo } = state;

    return (
      <Box className={classes.root}>
        <Box className={classes.panel}>
          <Typography className={classes.panelTitle} variant="h2">{betterhelpSsoL10n.linkTitle}</Typography>
          <Typography variant="body1">{betterhelpSsoL10n.linkBody}</Typography>
          <Box className={classes.actions}>
            <Button
              fullWidth
              variant="contained"
              color="legacyPrimary"
              size="large"
              onClick={() => handleConfirmLink(intent, redirectTo)}
            >
              {betterhelpSsoL10n.confirm}
            </Button>
            <Button variant="text" color="secondary" onClick={backToLogin}>
              {betterhelpSsoL10n.decline}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.panel}>
        <ErrorOutline className={classes.errorIcon} />
        <Typography className={classes.panelTitle} variant="h2">{betterhelpSsoL10n.errorTitle}</Typography>
        <Typography className={classes.panelTitle} variant="body1" color="text.secondary">{betterhelpSsoL10n.errorBody}</Typography>
        <Typography variant="body1" color="text.secondary">{betterhelpSsoL10n.errorContact}</Typography>
        <Box className={classes.actions}>
          <Button fullWidth variant="outlined" color="legacyPrimary" size="large" onClick={backToLogin}>
            {betterhelpSsoL10n.backToLogin}
          </Button>
        </Box>
      </Box>
    </Box>
  );
});
